import React, { useState, useEffect } from 'react';
import { Card, Table, Badge, Row, Col, Alert, Accordion } from 'react-bootstrap';
import { FaBox, FaRulerCombined, FaCalculator, FaBoxOpen } from 'react-icons/fa';
import axios from 'axios';
import config from "../../config";

const PackingMaterialDetailsCard = ({ items, packingMaterials }) => {
  // const [units, setUnits] = useState([]);
  const [unitConversions, setUnitConversions] = useState([]);

  useEffect(() => {
    const fetchUnitsData = async () => {
      try {
        const [
          // unitsRes,
           conversionsRes
        ] = await Promise.all([
          // axios.get(`${config.API_BASE_URL}/units`),
          axios.get(`${config.API_BASE_URL}/units/conversions`)
        ]);
        // setUnits(unitsRes.data);
        setUnitConversions(conversionsRes.data);
      } catch (err) {
        console.error('Error fetching units data:', err);
      }
    };

    if (items && items.length > 0) {
      fetchUnitsData();
    }
  }, [items]);

  // Helper function to convert units
  const convertUnits = (value, fromUnitCode, toUnitCode) => {
    if (value === 0 || !fromUnitCode || !toUnitCode || fromUnitCode === toUnitCode) {
      return value;
    }

    const conversion = unitConversions.find(
      conv =>
        (conv.fromUnitId.unitCode === fromUnitCode && conv.toUnitId.unitCode === toUnitCode) ||
        (conv.fromUnitId.unitCode === toUnitCode && conv.toUnitId.unitCode === fromUnitCode)
    );

    if (conversion) {
      if (conversion.fromUnitId.unitCode === fromUnitCode) {
        return value * conversion.multiplyFactor;
      } else {
        return value / conversion.multiplyFactor;
      }
    }

    // Fallback for common conversions if not found
    if ((fromUnitCode === 'gms' || fromUnitCode === 'g') && (toUnitCode === 'kg' || toUnitCode === 'kg')) {
      return fromUnitCode === toUnitCode ? value : (fromUnitCode === 'gms' ? value / 1000 : value * 1000);
    }

    console.warn(`Unit conversion not found: ${fromUnitCode} to ${toUnitCode}, using original value`);
    return value;
  };

  // Filter items that have packing material
  const itemsWithPacking = items.filter(item => item.packingBagType);

  if (itemsWithPacking.length === 0) {
    return null; // Don't show if no items have packing material
  }

  // Calculate packing details for each item
  const calculatePackingDetails = (item) => {
    if (!item.packingBagType || !item.quantity) {
      return null;
    }

    const packingMaterial = packingMaterials?.find(
      pm => pm._id === item.packingBagType._id || pm._id === item.packingBagType
    );

    if (!packingMaterial) {
      return null;
    }

    // Find the stuffing config for this product in the packing material
    const stuffingConfig = packingMaterial.itemWiseStuffing?.find(
      stuffing => stuffing.product._id === item.product._id || stuffing.product === item.product._id
    );

    if (!stuffingConfig) {
      return {
        packingMaterial,
        quantityPerBag: null,
        numberOfBags: null,
        stuffingConfig: null
      };
    }

    const quantityPerBag = stuffingConfig.quantity; // in stuffing unit
    const totalQuantity = item.quantity; // in item unit
    const isGross = item.grossOrNet === 'Gross';

    // Calculate bag weight with unit conversion
    const weightOfBag = packingMaterial.weightOfBag || 0;
    const bagWeightUnit = packingMaterial.weightOfBagUnit || 'gms'; // default assumption

    // Convert bag weight to match the item's unit
    let bagWeightPerBag = convertUnits(weightOfBag, bagWeightUnit, item.unit || 'kg');

    let numberOfBags, netWeight, grossWeight, bagWeightTotal;

    if (isGross) {
      // Gross: sale quantity includes product + bags
      const perBagTotalWeight = quantityPerBag + bagWeightPerBag;
      numberOfBags = Math.ceil(totalQuantity / perBagTotalWeight);
      bagWeightTotal = numberOfBags * bagWeightPerBag;
      netWeight = totalQuantity - bagWeightTotal; // Extract product weight from gross
      grossWeight = totalQuantity;
    } else {
      // Net: sale quantity is product weight only
      numberOfBags = Math.ceil(totalQuantity / quantityPerBag);
      bagWeightTotal = numberOfBags * bagWeightPerBag;
      netWeight = totalQuantity;
      grossWeight = netWeight + bagWeightTotal;
    }

    return {
      packingMaterial,
      quantityPerBag,
      numberOfBags,
      stuffingConfig,
      netWeight,
      grossWeight,
      bagWeightInKg: bagWeightPerBag, // renamed for clarity
      bagWeightTotal,
      weightType: item.grossOrNet || 'Net',
      isGross
    };
  };

  return (
    <Card className="mb-4 border-0 shadow-sm">
      <Card.Header className="bg-gradient-primary text-white">
        <h5 className="mb-0">
          <FaBoxOpen className="me-2" />
          Packing Material Details & Bag Calculation
        </h5>
      </Card.Header>
      <Card.Body>
        <Alert variant="info" className="mb-3 small">
          <FaCalculator className="me-2" />
          <strong>Note:</strong> Number of bags is automatically calculated based on product quantity and packing material capacity per bag. <strong>Gross</strong> weight includes bag weight in the order quantity, while <strong>Net</strong> weight shows product weight separately.
        </Alert>

        <Accordion defaultActiveKey="0" className="mb-3">
          {itemsWithPacking.map((item, index) => {
            const packingDetails = calculatePackingDetails(item);

            if (!packingDetails) {
              return null;
            }

            const {
              packingMaterial,
              quantityPerBag,
              numberOfBags,
              stuffingConfig,
              netWeight,
              grossWeight,
              bagWeightInKg,
              weightType
            } = packingDetails;

            return (
              <Accordion.Item eventKey={index.toString()} key={index} className="border-primary">
                <Accordion.Header className="bg-light">
                  <div className="d-flex justify-content-between align-items-center w-100 me-3">
                    <div>
                      <h6 className="mb-0">
                        <FaBox className="me-2 text-primary" />
                        {item.product?.productName} ({item.quality})
                      </h6>
                      <small className="text-muted">
                        Quantity: <strong>{item.quantity} {item.unit}</strong>
                      </small>
                    </div>
                    <div className="text-end">
                      <div className="d-flex align-items-center gap-2">
                        <Badge bg="success" className="fs-9">
                          <FaBox className="me-1" />
                          {numberOfBags || 0} bags required
                        </Badge>
                        <Badge bg="primary" className="fs-9">
                          Item #{index + 1}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </Accordion.Header>
                <Accordion.Body>
                    <Row className="g-3">
                      {/* Packing Material Info */}
                      <Col md={6} lg={3}>
                        <div className="p-3 bg-light rounded h-100">
                          <div className="d-flex align-items-center mb-2">
                            <FaBoxOpen className="text-primary me-2" size={20} />
                            <small className="text-muted">Packing Material</small>
                          </div>
                          <h6 className="mb-0">{packingMaterial.name}</h6>
                          <small className="text-muted">{packingMaterial.alias}</small>
                        </div>
                      </Col>

                      {/* Packing Size */}
                      <Col md={6} lg={3}>
                        <div className="p-3 bg-light rounded h-100">
                          <div className="d-flex align-items-center mb-2">
                            <FaRulerCombined className="text-info me-2" size={20} />
                            <small className="text-muted">Size/Dimensions</small>
                          </div>
                          <h6 className="mb-0">{item.packingSize || packingMaterial.size || 'N/A'}</h6>
                          <small className="text-muted">Bag dimensions</small>
                        </div>
                      </Col>

                      {/* Quantity Per Bag */}
                      <Col md={6} lg={3}>
                        <div className="p-3 bg-light rounded h-100">
                          <div className="d-flex align-items-center mb-2">
                            <FaBox className="text-success me-2" size={20} />
                            <small className="text-muted">Capacity Per Bag</small>
                          </div>
                          {stuffingConfig ? (
                            <>
                              <h6 className="mb-0 text-success">
                                {quantityPerBag} {stuffingConfig.unit || item.unit}
                              </h6>
                              <small className="text-muted">Product per bag</small>
                            </>
                          ) : (
                            <small className="text-warning">Not configured</small>
                          )}
                        </div>
                      </Col>

                      {/* Number of Bags */}
                      <Col md={6} lg={3}>
                        <div className="p-3 bg-success bg-opacity-10 rounded h-100 border border-success">
                          <div className="d-flex align-items-center mb-2">
                            <FaCalculator className="text-success me-2" size={20} />
                            <small className="text-muted">
                              <Badge bg="success" className="me-1">Auto-Calculated</Badge>
                            </small>
                          </div>
                          {numberOfBags ? (
                            <>
                              <h4 className="mb-0 text-success">
                                <FaBox className="me-1" />
                                {numberOfBags}
                              </h4>
                              <small className="text-muted">Total bags required</small>
                            </>
                          ) : (
                            <small className="text-warning">Cannot calculate</small>
                          )}
                        </div>
                      </Col>
                    </Row>

                    {/* Weight Details */}
                    {packingMaterial.weightOfBag && (
                      <Row className="mt-3">
                        <Col md={12}>
                          <Card className="border-info bg-info bg-opacity-10">
                            <Card.Body className="py-2">
                              <Row className="align-items-center">
                                <Col md={3}>
                                  <div className="text-center">
                                    {/* <FaWeight className="text-info mb-1" size={24} /> */}
                                    <div>
                                      <small className="text-muted d-block">Weight Type</small>
                                      <Badge bg={weightType === 'Gross' ? 'warning' : 'info'}>
                                        {weightType}
                                      </Badge>
                                    </div>
                                  </div>
                                </Col>
                                <Col md={3}>
                                  <div className="text-center">
                                    <small className="text-muted d-block">Bag Weight</small>
                                    <strong className="text-info">
                                      {packingMaterial.weightOfBag} {packingMaterial.weightOfBagUnit}
                                    </strong>
                                    <div>
                                      <small className="text-muted">
                                        ({bagWeightInKg ? bagWeightInKg.toFixed(3) : '0'} kg per bag)
                                      </small>
                                    </div>
                                  </div>
                                </Col>
                                <Col md={3}>
                                  <div className="text-center">
                                    <small className="text-muted d-block">Net Weight</small>
                                    <strong className="text-primary">
                                      {netWeight ? netWeight.toFixed(2) : '0'} {item.unit}
                                    </strong>
                                    <div>
                                      <small className="text-muted">(Product weight)</small>
                                    </div>
                                  </div>
                                </Col>
                                <Col md={3}>
                                  <div className="text-center">
                                    <small className="text-muted d-block">Gross Weight</small>
                                    <strong className="text-success">
                                      {grossWeight ? grossWeight.toFixed(2) : '0'} {item.unit}
                                    </strong>
                                    <div>
                                      <small className="text-muted">(Product + bag weight)</small>
                                    </div>
                                  </div>
                                </Col>
                              </Row>
                            </Card.Body>
                          </Card>
                        </Col>
                      </Row>
                    )}

                    {/* Calculation Formula */}
                    {quantityPerBag && numberOfBags && (
                      <Row className="mt-3">
                        <Col md={12}>
                          <Alert variant="secondary" className="mb-0 py-2">
                            <small>
                              <strong>{weightType} Calculation:</strong>{' '}
                              {weightType === 'Gross' ?
                                `${item.quantity} kg total ÷ (${quantityPerBag} kg/bag + ${(bagWeightInKg).toFixed(3)} kg/bag) = ${numberOfBags} bags | Net: ${numberOfBags} × ${quantityPerBag} kg = ${netWeight?.toFixed(2)} kg | Gross: ${grossWeight?.toFixed(2)} kg`
                                :
                                `${item.quantity} kg net ÷ ${quantityPerBag} kg/bag = ${numberOfBags} bags | Bag weight: ${numberOfBags} × ${(bagWeightInKg).toFixed(3)} kg = ${(numberOfBags * bagWeightInKg).toFixed(2)} kg | Gross: ${netWeight?.toFixed(2)} kg + ${(numberOfBags * bagWeightInKg).toFixed(2)} kg = ${grossWeight?.toFixed(2)} kg`
                              }
                            </small>
                          </Alert>
                        </Col>
                      </Row>
                    )}

                    {/* Warning if no stuffing config */}
                    {!stuffingConfig && (
                      <Row className="mt-3">
                        <Col md={12}>
                          <Alert variant="warning" className="mb-0 py-2">
                            <small>
                              ⚠️ <strong>Warning:</strong> This product is not configured in the packing material's stuffing list. 
                              Number of bags cannot be calculated. Please update packing material configuration.
                            </small>
                          </Alert>
                        </Col>
                      </Row>
                    )}
                </Accordion.Body>
              </Accordion.Item>
            );
          })}
        </Accordion>

        {/* Summary Table */}
        <Card className="border-0 bg-light mt-3">
          <Card.Header className="bg-dark text-white">
            <h6 className="mb-0">Packing Summary</h6>
          </Card.Header>
          <Card.Body className="p-0">
            <Table responsive bordered className="mb-0">
              <thead className="table-light">
                <tr>
                  <th>#</th>
                  <th>Product</th>
                  <th>Packing Material</th>
                  <th>Packing Size</th>
                  <th>Quantity</th>
                  <th>Per Bag</th>
                  <th>Bags Required</th>
                  <th>Weight Type</th>
                  <th>Bag Weight</th>
                  <th>Gross/Net Weight</th>
                </tr>
              </thead>
              <tbody>
                {itemsWithPacking.map((item, index) => {
                  const packingDetails = calculatePackingDetails(item);
                  
                  if (!packingDetails) {
                    return null;
                  }

                  const { 
                    packingMaterial, 
                    quantityPerBag, 
                    numberOfBags,
                    grossWeight,
                    weightType,
                    stuffingConfig
                  } = packingDetails;

                  return (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td>
                        <strong>{item.product?.productName}</strong>
                        <br />
                        <small className="text-muted">{item.quality}</small>
                      </td>
                      <td>
                        {packingMaterial.name}
                        <br />
                        <small className="text-muted">{packingMaterial.alias}</small>
                      </td>
                      <td>{item.packingSize || packingMaterial.size || '-'}</td>
                      <td>
                        <strong>{item.quantity}</strong> {item.unit}
                      </td>
                      <td>
                        {quantityPerBag ? (
                          <>
                            {quantityPerBag} {stuffingConfig?.unit || item.unit}
                          </>
                        ) : (
                          <span className="text-warning">N/A</span>
                        )}
                      </td>
                      <td>
                        {numberOfBags ? (
                          <Badge bg="success" className="fs-9">
                            <FaBox className="me-1" />
                            {numberOfBags}
                          </Badge>
                        ) : (
                          <span className="text-warning">-</span>
                        )}
                      </td>
                      <td>
                        <Badge bg={weightType === 'Gross' ? 'warning' : 'info'}>
                          {weightType}
                        </Badge>
                      </td>
                      <td>
                        {packingMaterial.weightOfBag ? (
                          <>
                            {packingMaterial.weightOfBag} {packingMaterial.weightOfBagUnit}
                          </>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td>
                        <strong>{grossWeight ? grossWeight.toFixed(2) : item.quantity}</strong> {item.unit}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      </Card.Body>

      <style jsx>{`
        .bg-gradient-primary {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        }
      `}</style>
    </Card>
  );
};

export default PackingMaterialDetailsCard;
